import { useState } from "react";
import { ApiError } from "../../api/client";
import type { Order } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { Dialog } from "../../ui/Dialog";
import { toKitchen } from "../printing/print-notices";

interface Props {
  /** The occupied table whose guests move. */
  from: string;
  onMoved: (order: Order) => void;
  onClose: () => void;
}

/** "Tafel X verplaatsen naar…": the free tables per room; everything ordered moves along. */
export function MoveTableDialog({ from, onMoved, onClose }: Props) {
  const { api, floor, openTables, orderSaved, toast, printStatus } = useTill();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const order = openTables.get(from);

  const move = async (to: string) => {
    if (!order) return;
    setBusy(true);
    setError(null);
    try {
      const moved = await api.request<Order>("POST", `/orders/${order.id}/move`, { body: { table: to } });
      orderSaved(moved);
      // prototype: "· melding naar de keuken" when the kitchen got VERPLAATST
      toast(toKitchen(moved.prints) ? t.tables.movedKitchen(from, to) : t.tables.moved(from, to));
      onMoved(moved);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      setBusy(false);
    }
  };

  const rooms = floor
    .map((room) => ({
      room,
      free: room.items.flatMap((item) =>
        item.kind === "table" && item.label !== null && item.label !== from && !openTables.has(item.label)
          ? [item.label]
          : [],
      ),
    }))
    .filter(({ free }) => free.length > 0);

  return (
    <Dialog title={t.tables.moveTo(from)} onClose={onClose}>
      {rooms.length === 0 && <p className="note">{t.tables.noFreeTable}</p>}
      {rooms.map(({ room, free }) => (
        <section key={room.id} aria-label={room.name}>
          <div className="zone">{room.name}</div>
          <div className="tables">
            {free.map((table) => (
              <button
                key={table}
                type="button"
                className="tbl"
                disabled={busy}
                aria-label={t.tables.label(table)}
                onClick={() => void move(table)}
              >
                {table}
                <small>{t.tables.free}</small>
              </button>
            ))}
          </div>
        </section>
      ))}
      {/* prototype: "De keuken krijgt een melding." (with printers) */}
      <p className="note">{(printStatus?.printers ?? 0) > 0 ? t.tables.moveNoteKitchen : t.tables.moveNote}</p>
      {error !== null && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
